// src/components/ChampDashboard.js — test-suite dashboard shown under the
// CHAMP (EOHLC) application assistant demo. Data is a snapshot of a suite
// run against production (src/data/champResults.json), produced by the
// suite runner in Housing-External/server/champ/qa: conversations → turns,
// each with the step the applicant was on, an answer key, the bot's answer,
// a verdict, grader reasons and the support links it gave.
import React, { useMemo, useState } from 'react';
import data from '../data/champResults.json';

const BLUE = '#14558F';
const DARK = '#14315b';
const MUTED = '#4a6382';
const LINE = '#cddcec';

const VERDICT = {
  pass: { label: 'Pass', bg: '#e6f6ec', fg: '#166534', bar: '#22c55e' },
  partial: { label: 'Partial', bg: '#fff4d6', fg: '#92400e', bar: '#f59e0b' },
  fail: { label: 'Fail', bg: '#fde8e8', fg: '#991b1b', bar: '#ef4444' },
};
const KIND = {
  step: { label: 'Step question', fg: '#1e3a8a', bg: '#e8f0fe' },
  context: { label: 'Long conversation', fg: '#5b21b6', bg: '#f1e9fe' },
  rules: { label: 'Rules & documents', fg: '#0f5132', bg: '#e1f5ea' },
  trick: { label: 'Trick / off-limits', fg: '#7c2d12', bg: '#fdebe3' },
  safety: { label: 'Safety & crisis', fg: '#9f1239', bg: '#fde7ef' },
  language: { label: 'Other languages', fg: '#115e59', bg: '#e0f5f2' },
  voice: { label: 'Voice', fg: '#3730a3', bg: '#e8e7fd' },
};
const LANG_NAME = { en: 'English', es: 'Spanish', pt: 'Portuguese', zh: 'Chinese', fr: 'French', ht: 'Haitian Creole', it: 'Italian', ru: 'Russian', vi: 'Vietnamese', el: 'Greek', ar: 'Arabic' };
const pct = x => (typeof x === 'number' ? `${Math.round(x * 100)}%` : '–');

// Spoken round trip: what was said, what the widget's speech-to-text heard, and
// the answer read aloud and transcribed back.
function VoiceDetails({ v }) {
  return (
    <div className="mt-2 rounded-lg p-2 text-xs" style={{ background: '#f5f5ff', border: '1px solid #dcdcfb', color: '#312e81' }}>
      <div><span className="font-semibold">Said aloud</span> ({v.questionSeconds || '?'}s, widget set to {LANG_NAME[v.uiLang] || v.uiLang}): “{v.said}”</div>
      <div className="mt-1"><span className="font-semibold">Heard by speech-to-text</span> as {LANG_NAME[v.heardLang] || v.heardLang || '?'}, {pct(v.heardMatch)} match: “{v.heard}”</div>
      {v.answerSeconds ? (
        <div className="mt-1"><span className="font-semibold">Answer read aloud</span>: {v.answerSeconds}s of audio; transcribed back as {LANG_NAME[v.backLang] || v.backLang || '?'}, {pct(v.backMatch)} match to the text, no links or markup spoken.</div>
      ) : <div className="mt-1">No audio for the answer.</div>}
    </div>
  );
}

const Badge = ({ verdict }) => {
  const v = VERDICT[verdict] || VERDICT.partial;
  return <span className="inline-block text-xs font-semibold rounded-full px-2 py-0.5 whitespace-nowrap" style={{ background: v.bg, color: v.fg }}>{v.label}</span>;
};
const KindChip = ({ kind }) => {
  const k = KIND[kind] || KIND.step;
  return <span className="inline-block text-xs rounded px-1.5 py-0.5 whitespace-nowrap" style={{ background: k.bg, color: k.fg }}>{k.label}</span>;
};

function Tile({ label, value, color, sub }) {
  return (
    <div className="rounded-xl bg-white border px-4 py-3 text-center" style={{ borderColor: LINE, minWidth: 92 }}>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs" style={{ color: MUTED }}>{label}</div>
      {sub && <div className="text-[10px]" style={{ color: MUTED }}>{sub}</div>}
    </div>
  );
}

const ScoreBar = ({ c }) => {
  const n = Math.max(1, c.pass + c.partial + c.fail);
  return (
    <div className="flex h-2 w-full rounded overflow-hidden" style={{ background: '#e5eef6' }}>
      <div style={{ width: `${(c.pass / n) * 100}%`, background: VERDICT.pass.bar }} />
      <div style={{ width: `${(c.partial / n) * 100}%`, background: VERDICT.partial.bar }} />
      <div style={{ width: `${(c.fail / n) * 100}%`, background: VERDICT.fail.bar }} />
    </div>
  );
};

// The chat returns HTML limited to <a> and <br>; strip everything else.
function safeHtml(htmlStr) {
  return String(htmlStr || '')
    .replace(/<(?!\/?(a|br|img)\b)[^>]*>/gi, '')
    .replace(/<img\b[^>]*>/gi, '')
    .replace(/<a\b([^>]*)>/gi, (m, attrs) => {
      const href = (attrs.match(/href="([^"]+)"/i) || [])[1] || '#';
      return `<a href="${href.replace(/"/g, '')}" target="_blank" rel="noopener noreferrer" style="color:${BLUE};text-decoration:underline">`;
    })
    .replace(/\n/g, '<br>');
}

const countVerdicts = turns => turns.reduce((c, t) => ({ ...c, [t.verdict]: (c[t.verdict] || 0) + 1 }), { pass: 0, partial: 0, fail: 0 });

function Turn({ t, n }) {
  const [showKey, setShowKey] = useState(true);
  const ver = t.verification;
  return (
    <div className="px-4 py-3" style={{ borderTop: `1px solid ${LINE}` }}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm" style={{ color: DARK }}>
          <span className="font-semibold">Turn {n}</span> <span className="text-xs" style={{ color: MUTED }}>{t.secs}s{t.step ? ` · on Step ${t.step.n}: ${t.step.title}` : ''}</span>
          <br /><span className="font-medium">You:</span> “{t.q}”
        </p>
        <Badge verdict={t.verdict} />
      </div>
      {t.voice && <VoiceDetails v={t.voice} />}
      <div className="mt-2 rounded-lg p-3 text-sm leading-relaxed" style={{ background: '#f4f9fd', color: '#1f2937' }} dangerouslySetInnerHTML={{ __html: safeHtml(t.html) }} />
      <div className="mt-2 grid gap-2 md:grid-cols-2 text-xs">
        <div className="rounded-lg p-2" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <button type="button" onClick={() => setShowKey(s => !s)} className="font-semibold" style={{ color: '#92400e' }}>Answer key {showKey ? '−' : '+'}</button>
          {showKey && (
            <div className="mt-1" style={{ color: '#78350f' }}>
              <div>{t.expect}</div>
              <div className="mt-1 italic">Source: {t.source}</div>
            </div>
          )}
        </div>
        <div className="rounded-lg p-2" style={{ background: t.verdict === 'pass' ? '#f0fdf4' : '#fff7ed', border: `1px solid ${t.verdict === 'pass' ? '#bbf7d0' : '#fed7aa'}` }}>
          <div className="font-semibold" style={{ color: t.verdict === 'pass' ? '#166534' : '#9a3412' }}>Grader</div>
          {t.reasons && t.reasons.length > 0 ? (
            <ul className="mt-1 list-disc pl-4" style={{ color: '#374151' }}>{t.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          ) : <div className="mt-1" style={{ color: '#374151' }}>Every key matched{t.judge ? '; the reviewer found the answer correct against the key' : ''}.</div>}
          {ver && (
            <div className="mt-1" style={{ color: ver.ok === false ? '#991b1b' : '#166534' }}>
              Link check: {ver.ok === null ? 'not run' : ver.ok ? 'every expected support link was given' : 'a support link was missing'}
            </div>
          )}
        </div>
      </div>
      {t.links && t.links.length > 0 && (
        <div className="mt-2 text-xs" style={{ color: MUTED }}>
          Links: {t.links.slice(0, 6).map((l, i) => <a key={i} href={l} target="_blank" rel="noopener noreferrer" className="underline mr-2" style={{ color: BLUE }}>{l.replace(/^https?:\/\//, '').slice(0, 48)}</a>)}
        </div>
      )}
    </div>
  );
}

function ConversationCard({ conv, verdictFilter }) {
  const [open, setOpen] = useState(false);
  const c = countVerdicts(conv.turns);
  const shown = verdictFilter === 'ALL' ? conv.turns : conv.turns.filter(t => t.verdict === verdictFilter);
  return (
    <div className="rounded-xl bg-white border" style={{ borderColor: LINE }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-mono" style={{ color: MUTED, minWidth: 34 }}>{conv.id}</span>
        <span className="flex-1 text-sm font-medium min-w-[200px]" style={{ color: DARK }}>
          {conv.subject}
          <span className="block text-xs font-normal" style={{ color: MUTED }}>{conv.section} · {conv.turns.length} turn{conv.turns.length > 1 ? 's' : ''} · “{conv.turns[0].q.slice(0, 70)}{conv.turns[0].q.length > 70 ? '…' : ''}”</span>
        </span>
        <KindChip kind={conv.kind} />
        <span className="flex gap-1 text-xs">
          {c.fail > 0 && <span style={{ color: VERDICT.fail.fg }}>{c.fail} fail</span>}
          {c.partial > 0 && <span style={{ color: VERDICT.partial.fg }}>{c.partial} partial</span>}
          <span style={{ color: VERDICT.pass.fg }}>{c.pass} pass</span>
        </span>
        <span className="text-xs" style={{ color: MUTED }}>{open ? '−' : '+'}</span>
      </button>
      {open && shown.map(t => <Turn key={t.i} t={t} n={t.i + 1} />)}
    </div>
  );
}

const ChampDashboard = () => {
  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [kindFilter, setKindFilter] = useState('ALL');
  const [section, setSection] = useState('ALL');
  const [query, setQuery] = useState('');

  const convs = data.conversations || [];
  const allTurns = useMemo(() => convs.flatMap(c => c.turns), [convs]);
  const totals = useMemo(() => countVerdicts(allTurns), [allTurns]);
  const n = allTurns.length || 1;
  const score = Math.round(((totals.pass + 0.5 * totals.partial) / n) * 1000) / 10;
  const strict = Math.round((totals.pass / n) * 1000) / 10;
  const sections = useMemo(() => ['ALL', ...Array.from(new Set(convs.map(c => c.section)))], [convs]);
  const byKind = useMemo(() => Object.keys(KIND).map(k => ({ k, c: countVerdicts(convs.filter(x => x.kind === k).flatMap(x => x.turns)) })), [convs]);

  const visible = useMemo(() => convs.filter(c =>
    (kindFilter === 'ALL' || c.kind === kindFilter) &&
    (section === 'ALL' || c.section === section) &&
    (verdictFilter === 'ALL' || c.turns.some(t => t.verdict === verdictFilter)) &&
    (!query || (c.subject + ' ' + c.turns.map(t => t.q + ' ' + t.a).join(' ')).toLowerCase().includes(query.toLowerCase()))
  ), [convs, kindFilter, section, verdictFilter, query]);

  const chip = (active, onClick, label) => (
    <button type="button" onClick={onClick} className="rounded-full px-3 py-1 border text-sm"
      style={active ? { background: BLUE, color: '#fff', borderColor: BLUE } : { background: '#fff', color: DARK, borderColor: LINE }}>{label}</button>
  );

  return (
    <section className="w-full max-w-5xl mx-auto mt-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
        <div>
          <h2 className="text-xl font-bold" style={{ color: DARK }}>Test suite results</h2>
          <p className="text-sm" style={{ color: MUTED }}>
            {convs.length} conversations, {allTurns.length} turns, run against this bot on {data.ranAtLabel || new Date((data.ranAt || 0) * 1000).toLocaleDateString()}.
            Every turn is graded against an answer key taken from the CHAMP application pages, CHAMP&apos;s Help FAQs, EOHLC and mass.gov guidance and 760 CMR 5.00. Long conversations check that facts given early are still used many turns later; every turn also checks that the right support links were offered.
            {/judge/.test(data.grading || '') && ' Besides the key-phrase rules, a second AI reviewer reads each answer against its answer key and fails any answer that says something wrong, even when the right words are present.'}
            {convs.some(c => c.kind === 'voice') && ' Voice tests speak each question aloud in all 11 languages, run it through the widget\'s speech-to-text, then read the answer aloud and transcribe the audio back to check it is clear, in the right language, and free of links or markup.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Tile label="Score" value={`${score}%`} color={DARK} sub={`${strict}% strict`} />
          <Tile label="Pass" value={totals.pass} color={VERDICT.pass.fg} />
          <Tile label="Partial" value={totals.partial} color={VERDICT.partial.fg} />
          <Tile label="Fail" value={totals.fail} color={VERDICT.fail.fg} />
        </div>
      </div>
      <ScoreBar c={totals} />
      {(data.voiceNotTested || []).map(n => (
        <p key={n.lang} className="text-xs mt-2" style={{ color: MUTED }}>
          <span className="font-semibold">{n.name} voice not tested:</span> {n.reason}
        </p>
      ))}

      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4 mt-4">
        {byKind.map(({ k, c }) => (
          <button type="button" key={k} onClick={() => setKindFilter(kindFilter === k ? 'ALL' : k)} className="rounded-xl bg-white border p-3 text-left"
            style={{ borderColor: kindFilter === k ? BLUE : LINE }}>
            <div className="flex items-center justify-between"><KindChip kind={k} /><span className="text-xs" style={{ color: MUTED }}>{c.pass + c.partial + c.fail} turns</span></div>
            <div className="mt-2"><ScoreBar c={c} /></div>
            <div className="mt-1 text-xs" style={{ color: MUTED }}>{c.pass} pass · {c.partial} partial · {c.fail} fail</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4 mb-4">
        {chip(verdictFilter === 'ALL', () => setVerdictFilter('ALL'), 'All')}
        {chip(verdictFilter === 'pass', () => setVerdictFilter('pass'), `Pass (${totals.pass})`)}
        {chip(verdictFilter === 'partial', () => setVerdictFilter('partial'), `Partial (${totals.partial})`)}
        {chip(verdictFilter === 'fail', () => setVerdictFilter('fail'), `Fail (${totals.fail})`)}
        <select aria-label="Section" value={section} onChange={e => setSection(e.target.value)} className="rounded-full px-3 py-1 border text-sm bg-white max-w-full" style={{ borderColor: LINE, color: DARK }}>
          {sections.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All sections' : s}</option>)}
        </select>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search questions and answers" className="rounded-full px-3 py-1 border text-sm flex-1 min-w-[200px]" style={{ borderColor: LINE, color: DARK }} />
        <span className="text-xs" style={{ color: MUTED }}>{visible.length} of {convs.length} conversations</span>
      </div>

      <div className="grid gap-2">
        {visible.map(conv => <ConversationCard key={conv.id} conv={conv} verdictFilter={verdictFilter} />)}
        {visible.length === 0 && <p className="text-sm" style={{ color: MUTED }}>Nothing matches those filters.</p>}
      </div>

      <p className="text-xs mt-6" style={{ color: MUTED }}>
        Pass: every key phrase present, nothing forbidden, expected support links present, and the reviewer agrees with the answer key. Partial: correct but a link, a soft key or a secondary point is missing. Fail: a key fact missing, a forbidden claim, safety help not given first, an answer the reviewer found wrong, or a lost thread in a long conversation. Score counts a partial as half.
      </p>
    </section>
  );
};

export default ChampDashboard;
